import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { AlertCircle, ArrowDown, ArrowUp, Archive, Plus, Save, Star, Trash2, Workflow as WorkflowIcon } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useDiscardGuard } from '../../components/DiscardGuard'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { AvatarStack } from '../../components/AvatarStack'
import { decisionLabels } from '../../components/badges'
import { WorkflowTimeline } from '../../components/WorkflowTimeline'
import { platformApi } from '../../api'
import type { AssigneeSpec, DecisionKind, Workflow, WorkflowDefinition } from '../../types'
import { useAdmin } from './AdminData'
import { AssigneeEditor } from './AssigneeEditor'
import {
    ALL_DECISIONS, emptyDefinition, groupIdsInDefinition, moveItem, newStep, nextKey, previewPeople, previewSteps,
    removeAt, renumberSteps, replaceAt, validateWorkflowDraft,
} from './model'

interface DraftStep { key: string; name: string; mode: 'any' | 'all'; assignees: AssigneeSpec[] }
interface Draft {
    name: string
    is_default: boolean
    archived: boolean
    steps: DraftStep[]
    decisions: DecisionKind[]
    requireAttestation: boolean
    shareComment: boolean
    autoEmail: boolean
    allowSelfReview: boolean
}

function toDraft(w: Workflow | null): Draft {
    const d = w?.definition ?? emptyDefinition()
    return {
        name: w?.name ?? '',
        is_default: w?.is_default ?? false,
        archived: w?.archived ?? false,
        steps: d.steps.slice().sort((a, b) => a.order - b.order)
            .map((s) => ({ key: nextKey('s'), name: s.name, mode: s.mode, assignees: s.assignees })),
        decisions: d.decisions,
        requireAttestation: !!d.requireAttestation,
        shareComment: !!d.shareComment,
        autoEmail: !!d.autoEmail,
        allowSelfReview: !!d.allowSelfReview,
    }
}

function toDefinition(d: Draft): WorkflowDefinition {
    return {
        steps: renumberSteps(d.steps.map((s, i) => ({ order: i + 1, name: s.name.trim(), mode: s.mode, assignees: s.assignees }))),
        decisions: ALL_DECISIONS.filter((x) => d.decisions.includes(x)),
        requireAttestation: d.requireAttestation,
        shareComment: d.shareComment,
        autoEmail: d.autoEmail,
        allowSelfReview: d.allowSelfReview,
    }
}

// ---------------------------------------------------------------- editor

function WorkflowEditor({ workflow, open, onOpenChange, onSaved }: {
    workflow: Workflow | null
    open: boolean
    onOpenChange: (o: boolean) => void
    onSaved: () => void
}) {
    const admin = useAdmin()
    const [draft, setDraft] = useState<Draft>(() => toDraft(workflow))
    const [initial, setInitial] = useState(() => JSON.stringify(draft))
    const [saving, setSaving] = useState(false)
    const [problems, setProblems] = useState<string[]>([])
    const [error, setError] = useState('')
    const focusId = useRef<string | null>(null)

    useEffect(() => {
        if (open) {
            const d = toDraft(workflow)
            setDraft(d); setInitial(JSON.stringify(d)); setProblems([]); setError('')
        }
    }, [open, workflow])
    const guard = useDiscardGuard(JSON.stringify(draft) !== initial, onOpenChange)

    // Load members of every group the draft references so the preview shows avatars.
    const groupIds = useMemo(() => groupIdsInDefinition(toDefinition(draft)), [draft])
    const { ensureMembers } = admin
    useEffect(() => { ensureMembers(groupIds) }, [groupIds, ensureMembers])

    useEffect(() => {
        if (focusId.current) {
            document.getElementById(focusId.current)?.focus()
            focusId.current = null
        }
    })

    const ctx = useMemo(() => ({
        groups: admin.groups, userRefFields: admin.userRefFields, conditionFields: admin.conditionFields,
    }), [admin.groups, admin.userRefFields, admin.conditionFields])
    const preview = useMemo(
        () => previewSteps(toDefinition(draft), { ...ctx, members: admin.members }),
        [draft, ctx, admin.members],
    )

    const patch = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }))
    const patchStep = (i: number, p: Partial<DraftStep>) => setDraft((d) => ({ ...d, steps: replaceAt(d.steps, i, { ...d.steps[i], ...p }) }))
    const moveStep = (i: number, to: number) => {
        const key = draft.steps[i].key
        focusId.current = `step-${key}-${to < i ? 'up' : 'down'}`
        setDraft((d) => ({ ...d, steps: moveItem(d.steps, i, to) }))
    }
    const toggleDecision = (dc: DecisionKind, on: boolean) =>
        patch({ decisions: on ? [...draft.decisions, dc] : draft.decisions.filter((x) => x !== dc) })

    const save = async () => {
        setError('')
        const def = toDefinition(draft)
        const local = validateWorkflowDraft(draft.name, def)
        setProblems(local)
        if (local.length) return
        setSaving(true)
        try {
            if (workflow) {
                await platformApi.updateWorkflow(workflow.id, { name: draft.name.trim(), definition: def, is_default: draft.is_default, archived: draft.archived })
            } else {
                await platformApi.createWorkflow({ name: draft.name.trim(), definition: def, is_default: draft.is_default })
            }
            toast.success(workflow ? 'Workflow saved' : 'Workflow created')
            onSaved()
            onOpenChange(false)
        } catch (e) {
            // requestJSON keeps only the message; surface server problems if a richer error is thrown.
            const server = (e as { problems?: string[] }).problems
            if (server?.length) setProblems(server)
            setError(e instanceof Error ? e.message : 'Could not save the workflow')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Sheet open={open} onOpenChange={guard.requestClose}>
            <SheetContent className="w-full gap-0 data-[side=right]:sm:max-w-5xl" {...guard.contentProps}>
                <SheetHeader className="border-b">
                    <SheetTitle>{workflow ? `Edit ${workflow.name}` : 'New workflow'}</SheetTitle>
                    <SheetDescription>Steps run in order. Each step needs at least one assignee.</SheetDescription>
                </SheetHeader>

                <div className="grid flex-1 gap-6 overflow-y-auto p-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
                    <div className="flex min-w-0 flex-col gap-6">
                        {(problems.length > 0 || error) && (
                            <Alert variant="destructive">
                                <AlertCircle />
                                <AlertTitle>{error || 'Fix these before saving'}</AlertTitle>
                                {problems.length > 0 && (
                                    <AlertDescription>
                                        <ul className="list-disc pl-4">{problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
                                    </AlertDescription>
                                )}
                            </Alert>
                        )}

                        <FieldGroup>
                            <Field>
                                <FieldLabel htmlFor="wf-name">Name</FieldLabel>
                                <Input id="wf-name" value={draft.name} onChange={(e) => patch({ name: e.target.value })} />
                            </Field>
                            <Field orientation="horizontal">
                                <Switch id="wf-default" checked={draft.is_default} onCheckedChange={(v) => patch({ is_default: v })} />
                                <div className="flex flex-col gap-0.5">
                                    <FieldLabel htmlFor="wf-default">Default workflow</FieldLabel>
                                    <FieldDescription>Catches every submission that no routing rule matches.</FieldDescription>
                                </div>
                            </Field>
                            {workflow && (
                                <Field orientation="horizontal">
                                    <Switch id="wf-archived" checked={draft.archived} onCheckedChange={(v) => patch({ archived: v })} />
                                    <div className="flex flex-col gap-0.5">
                                        <FieldLabel htmlFor="wf-archived">Archived</FieldLabel>
                                        <FieldDescription>Archived workflows are kept for history but no longer used.</FieldDescription>
                                    </div>
                                </Field>
                            )}
                            <FieldSet>
                                <FieldLegend variant="label">Decisions reviewers can record</FieldLegend>
                                <FieldGroup className="gap-3">
                                    {ALL_DECISIONS.map((dc) => (
                                        <Field key={dc} orientation="horizontal">
                                            <Checkbox id={`dc-${dc}`} checked={draft.decisions.includes(dc)} onCheckedChange={(v) => toggleDecision(dc, v === true)} />
                                            <FieldLabel htmlFor={`dc-${dc}`}>{decisionLabels[dc]}</FieldLabel>
                                        </Field>
                                    ))}
                                    <Field orientation="horizontal">
                                        <Switch id="wf-self" checked={draft.allowSelfReview} onCheckedChange={(v) => patch({ allowSelfReview: v })} />
                                        <FieldLabel htmlFor="wf-self">Allow people to review their own requests (otherwise they are left out of every step)</FieldLabel>
                                    </Field>
                                </FieldGroup>
                            </FieldSet>
                            <FieldSet>
                                <FieldLegend variant="label">Options</FieldLegend>
                                <FieldGroup className="gap-3">
                                    <Field orientation="horizontal">
                                        <Switch id="wf-att" checked={draft.requireAttestation} onCheckedChange={(v) => patch({ requireAttestation: v })} />
                                        <FieldLabel htmlFor="wf-att">Require a comment with every decision</FieldLabel>
                                    </Field>
                                    <Field orientation="horizontal">
                                        <Switch id="wf-share" checked={draft.shareComment} onCheckedChange={(v) => patch({ shareComment: v })} />
                                        <FieldLabel htmlFor="wf-share">Share the decision comment with the submitter</FieldLabel>
                                    </Field>
                                    <Field orientation="horizontal">
                                        <Switch id="wf-email" checked={draft.autoEmail} onCheckedChange={(v) => patch({ autoEmail: v })} />
                                        <FieldLabel htmlFor="wf-email">Email the submitter about receipt and decisions (reviewers are always emailed)</FieldLabel>
                                    </Field>
                                </FieldGroup>
                            </FieldSet>
                        </FieldGroup>

                        <section className="flex flex-col gap-3" aria-labelledby="steps-heading">
                            <div className="flex items-center justify-between">
                                <h3 id="steps-heading" className="text-sm font-semibold">Steps</h3>
                                <Button type="button" size="sm" variant="outline"
                                    onClick={() => setDraft((d) => ({ ...d, steps: [...d.steps, { key: nextKey('s'), ...newStep(d.steps.length + 1) }] }))}>
                                    <Plus data-icon="inline-start" /> Add step
                                </Button>
                            </div>
                            {draft.steps.length === 0 && (
                                <Empty className="border">
                                    <EmptyHeader><EmptyTitle>No steps</EmptyTitle><EmptyDescription>Add the first approval step.</EmptyDescription></EmptyHeader>
                                </Empty>
                            )}
                            <ol className="flex flex-col gap-3">
                                {draft.steps.map((s, i) => (
                                    <li key={s.key}>
                                        <Card size="sm">
                                            <CardHeader>
                                                <CardTitle>Step {i + 1}</CardTitle>
                                                <CardDescription>{s.mode === 'all' ? 'Everyone assigned must approve' : 'Any one assignee can approve'}</CardDescription>
                                                <div className="col-start-2 row-span-2 row-start-1 flex gap-1 self-start justify-self-end">
                                                    <Button type="button" variant="ghost" size="icon-sm" id={`step-${s.key}-up`} disabled={i === 0}
                                                        aria-label={`Move step ${i + 1} up`} onClick={() => moveStep(i, i - 1)}><ArrowUp /></Button>
                                                    <Button type="button" variant="ghost" size="icon-sm" id={`step-${s.key}-down`} disabled={i === draft.steps.length - 1}
                                                        aria-label={`Move step ${i + 1} down`} onClick={() => moveStep(i, i + 1)}><ArrowDown /></Button>
                                                    <Button type="button" variant="ghost" size="icon-sm" aria-label={`Delete step ${i + 1}`}
                                                        onClick={() => setDraft((d) => ({ ...d, steps: removeAt(d.steps, i) }))}><Trash2 /></Button>
                                                </div>
                                            </CardHeader>
                                            <CardContent>
                                                <FieldGroup>
                                                    <Field>
                                                        <FieldLabel htmlFor={`sn-${s.key}`}>Step name</FieldLabel>
                                                        <Input id={`sn-${s.key}`} value={s.name} onChange={(e) => patchStep(i, { name: e.target.value })} />
                                                    </Field>
                                                    <Field>
                                                        <FieldLabel>Approval mode</FieldLabel>
                                                        <ToggleGroup type="single" variant="outline" value={s.mode} aria-label="Approval mode"
                                                            onValueChange={(v) => v && patchStep(i, { mode: v as 'any' | 'all' })}>
                                                            <ToggleGroupItem value="any">Any one approver</ToggleGroupItem>
                                                            <ToggleGroupItem value="all">Everyone must approve</ToggleGroupItem>
                                                        </ToggleGroup>
                                                    </Field>
                                                    <Field>
                                                        <FieldLabel>Assignees</FieldLabel>
                                                        <AssigneeEditor value={s.assignees} ctx={ctx} onChange={(assignees) => patchStep(i, { assignees })} />
                                                    </Field>
                                                </FieldGroup>
                                            </CardContent>
                                        </Card>
                                    </li>
                                ))}
                            </ol>
                        </section>
                    </div>

                    <aside className="min-w-0 lg:sticky lg:top-0 lg:self-start" aria-label="Live preview">
                        <Card size="sm">
                            <CardHeader>
                                <CardTitle>Live preview</CardTitle>
                                <CardDescription>Rules appear as placeholders; they resolve per submission.</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <WorkflowTimeline steps={preview} orientation="vertical" />
                            </CardContent>
                        </Card>
                    </aside>
                </div>

                <SheetFooter className="border-t sm:flex-row sm:justify-end">
                    <Button type="button" variant="outline" onClick={() => guard.requestClose(false)}>Cancel</Button>
                    <Button type="button" onClick={save} disabled={saving}>
                        {saving ? <Spinner data-icon="inline-start" /> : <Save data-icon="inline-start" />}
                        {workflow ? 'Save changes' : 'Create workflow'}
                    </Button>
                </SheetFooter>
            </SheetContent>
            {guard.dialog}
        </Sheet>
    )
}

// ---------------------------------------------------------------- tab

export default function WorkflowsTab() {
    const admin = useAdmin()
    const [editing, setEditing] = useState<Workflow | null>(null)
    const [open, setOpen] = useState(false)
    const { ensureMembers, workflows } = admin

    useEffect(() => {
        ensureMembers([...new Set(workflows.flatMap((w) => groupIdsInDefinition(w.definition)))])
    }, [workflows, ensureMembers])

    const ctx = { groups: admin.groups, userRefFields: admin.userRefFields, members: admin.members }

    const edit = (w: Workflow | null) => { setEditing(w); setOpen(true) }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">Approval workflows decide who reviews a submission, in which order.</p>
                <Button type="button" onClick={() => edit(null)}><Plus data-icon="inline-start" /> New workflow</Button>
            </div>

            {admin.loading ? (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40" />)}
                </div>
            ) : workflows.length === 0 ? (
                <Empty className="border">
                    <EmptyHeader>
                        <EmptyMedia variant="icon"><WorkflowIcon /></EmptyMedia>
                        <EmptyTitle>No workflows yet</EmptyTitle>
                        <EmptyDescription>Create a workflow and mark it as the default so submissions get routed.</EmptyDescription>
                    </EmptyHeader>
                </Empty>
            ) : (
                <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {workflows.map((w) => {
                        const people = [...new Map(w.definition.steps.flatMap((s) => previewPeople(s.assignees, ctx)).map((p) => [p.id, p])).values()]
                        return (
                            <li key={w.id}>
                                <Card className="h-full">
                                    <CardHeader>
                                        <CardTitle className="flex flex-wrap items-center gap-2">
                                            {w.name}
                                            {w.is_default && <Badge><Star data-icon="inline-start" /> Default</Badge>}
                                            {w.archived && <Badge variant="outline"><Archive data-icon="inline-start" /> Archived</Badge>}
                                        </CardTitle>
                                        <CardDescription>
                                            {w.definition.steps.length} {w.definition.steps.length === 1 ? 'step' : 'steps'}
                                            {' · '}{w.definition.steps.map((s) => s.name).join(' → ')}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <AvatarStack people={people} />
                                    </CardContent>
                                    <CardFooter>
                                        <Button type="button" variant="outline" size="sm" onClick={() => edit(w)}>Edit</Button>
                                    </CardFooter>
                                </Card>
                            </li>
                        )
                    })}
                </ul>
            )}

            <WorkflowEditor workflow={editing} open={open} onOpenChange={setOpen} onSaved={() => void admin.reloadWorkflows()} />
        </div>
    )
}
